import _ from 'lodash';

type MetricListContent = Pick<
  Studio.ChartContent,
  'visType' | 'description'
> & {
  table?: Studio.TableOptions;
};

type Item = Studio.TableMetricListItem;

const withoutUndefined = <T extends object>(value: T): T =>
  _.omitBy(value, _.isUndefined) as T;

// The saved filters the legacy editor gives an item (AddMetricButton)
const ITEM_SAVED_FILTER_SOURCE = 'metric-list-item';
const ITEM_SAVED_FILTER_DATE_FIELD = 'date';

// Whether a Table lists metrics, saved as the legacy Metric List
export const isMetricListTable = (content: MetricListContent): boolean =>
  content.visType === 'table' && content.table?.rows === 'metricList';

// Its items with a metric chosen (one just added has none yet)
export const getMetricListItems = (content: MetricListContent): Item[] =>
  isMetricListTable(content)
    ? (content.table?.metricList?.items ?? []).filter((item) => !!item.metricId)
    : [];

// The metrics a metric list shows, in row order
export const getMetricListMetricIds = (content: MetricListContent): string[] =>
  _.uniq(getMetricListItems(content).map((item) => item.metricId));

// A metric list's table with a row for the metric added at the end (or
// as it was, if it has one already)
export const withMetricListItem = ({
  table,
  metricId,
  newId,
}: {
  table: Studio.TableOptions;
  metricId: string;
  newId: () => string;
}): Studio.TableOptions => {
  const items = table.metricList?.items ?? [];
  if (items.some((item) => item.metricId === metricId)) {
    return table;
  }
  return {
    ...table,
    metricList: {
      ...table.metricList,
      items: [...items, { id: newId(), metricId }],
    },
  };
};

// What a draft item's filters are drawn from until they're saved: a saved
// filter of its own, under an id that's never stored
export const toDraftSavedFilterId = (itemId: string): string =>
  `studio-draft-${itemId}`;

// An item's filters as the saved filter the legacy list reads them from
export const toItemSavedFilter = ({
  item,
  id,
}: {
  item: Item;
  id: string;
}): LegacySavedFilter => ({
  version: '2',
  id,
  scope: item.filters ?? [],
  drillDowns: item.drillDowns ?? [],
  source: ITEM_SAVED_FILTER_SOURCE,
  dateField: ITEM_SAVED_FILTER_DATE_FIELD,
});

// Whether the saved filter holds the item's filters as they are
const isSameFilters = ({
  item,
  savedFilter,
}: {
  item: Item;
  savedFilter: LegacySavedFilter | undefined;
}): boolean =>
  !!savedFilter &&
  _.isEqual(savedFilter.scope ?? [], item.filters ?? []) &&
  _.isEqual(savedFilter.drillDowns ?? [], item.drillDowns ?? []);

// A metric list's items as the legacy Metric List has them, each with the
// saved filter it reads its filters from: its own while unchanged, else a
// draft one (saved under a new id) or, without filters, still a fresh one,
// as the legacy list needs one per item
const toLegacyItems = ({
  items,
  savedFilters,
  resolveName,
  newSavedFilterId,
}: {
  items: Item[];
  savedFilters: Record<string, LegacySavedFilter | undefined>;
  resolveName: (metricId: string) => string;
  newSavedFilterId: (item: Item) => string;
}): { list: MetricListItemType[]; savedFilterWrites: LegacySavedFilter[] } => {
  const savedFilterWrites: LegacySavedFilter[] = [];
  const list = items.map((item): MetricListItemType => {
    const kept =
      item.savedFilterId &&
      isSameFilters({ item, savedFilter: savedFilters[item.savedFilterId] })
        ? item.savedFilterId
        : undefined;
    const savedFilterId = kept ?? newSavedFilterId(item);
    if (!kept) {
      savedFilterWrites.push(toItemSavedFilter({ item, id: savedFilterId }));
    }
    return withoutUndefined({
      id: item.id,
      name: item.name || resolveName(item.metricId),
      metricId: item.metricId,
      savedFilterId,
      reportDrillDownId: item.reportDrillDownId,
      boardDrillDownId: item.boardDrillDownId,
      isEntityFilterEnabled: !!item.isEntityFilterEnabled,
      isDefaultFiltersDisabled: !!item.isDefaultFiltersDisabled,
    });
  });
  return { list, savedFilterWrites };
};

// The legacy Metric List a metric list Table draws as, before it's saved:
// its items' filters in draft saved filters (see DraftSavedFiltersContext)
// rather than the stored ones, so a change shows straight away
export const buildMetricListDefinition = ({
  content,
  id,
  name,
  resolveName,
}: {
  content: MetricListContent;
  id: string;
  name: string;
  resolveName: (metricId: string) => string;
}):
  | {
      chartDefinition: MetricListGadgetType;
      draftSavedFilters: Record<string, LegacySavedFilter>;
    }
  | { missing: string } => {
  const items = getMetricListItems(content);
  if (items.length === 0) {
    return { missing: 'Add a metric to the list' };
  }
  const { list, savedFilterWrites } = toLegacyItems({
    items,
    // Every item reads a draft, whether or not its filters changed
    savedFilters: {},
    resolveName,
    newSavedFilterId: (item) => toDraftSavedFilterId(item.id),
  });
  return {
    chartDefinition: {
      id,
      name,
      type: 'Metric List',
      list,
      listOrder: list.map((item) => item.id),
      isBonusPeriodMode: false,
      dateField: ITEM_SAVED_FILTER_DATE_FIELD,
      createdBy: '',
      createdOn: '',
      updatedBy: '',
      updatedOn: '',
    },
    draftSavedFilters: _.keyBy(savedFilterWrites, 'id'),
  };
};

// The list and saved filters to write for a metric list Table: the list
// under its own id (or a new one), its items' saved filters only where
// their filters changed (or they're new)
export const planMetricListWrites = ({
  content,
  name,
  base,
  savedFilters,
  dates,
  comparison,
  gadgetSettings,
  resolveName,
  newId,
  userId,
  now,
}: {
  content: MetricListContent;
  name: string;
  // The list as it's saved, if it is
  base: MetricListGadgetType | undefined;
  // The account's saved filters, to tell which are unchanged
  savedFilters: Record<string, LegacySavedFilter | undefined>;
  dates: DateFiltered;
  comparison: PersistedComparisonType | undefined;
  gadgetSettings: Studio.GadgetSettings | undefined;
  resolveName: (metricId: string) => string;
  newId: () => string;
  userId: string;
  now: string;
}): { list: MetricListGadgetType; savedFilterWrites: LegacySavedFilter[] } => {
  const { list, savedFilterWrites } = toLegacyItems({
    items: getMetricListItems(content),
    savedFilters,
    resolveName,
    newSavedFilterId: () => newId(),
  });
  const id = content.table?.metricList?.metricListId ?? base?.id ?? newId();
  return {
    list: withoutUndefined({
      createdBy: userId,
      createdOn: now,
      ...base,
      id,
      name,
      type: 'Metric List' as const,
      list,
      listOrder: list.map((item) => item.id),
      isBonusPeriodMode: !!gadgetSettings?.isBonusPeriodMode,
      weekStartsOnOverride: gadgetSettings?.weekStartsOnOverride,
      comparison,
      // Every date field is set, so an edit clears the ones no longer used
      dateField: dates.dateField,
      dateRange: dates.dateRange,
      relativeDateRange: dates.relativeDateRange,
      advancedRelativeDateRange: dates.advancedRelativeDateRange,
      updatedBy: userId,
      updatedOn: now,
    }),
    savedFilterWrites,
  };
};
